/* ═══════════════════════════════════════════════════════════════
   DayExpansionDrawer — optional per-day detail for one (member,
   project, week) cell. The Plan grid's default unit is the WEEK (D3);
   this is the escape hatch when a PM needs an uneven Mon–Sun split
   instead of the even Mon–Fri default. Kit Dialog — no hand-rolled
   modal, real focus trap / Esc / outside-click via Radix.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { weekDays, formatShort } from './utils';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function DayExpansionDrawer({
  open,
  onOpenChange,
  title,
  weekStart,
  hoursByDay,
  readOnly,
  saving,
  onSave,
  onClear,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  weekStart: string;
  /** Current per-day hours (yyyy-mm-dd -> hours), 0 for days with nothing. */
  hoursByDay: Record<string, number>;
  readOnly: boolean;
  saving: boolean;
  /** Save the full 7-day map — the caller writes each changed day
   *  individually via the existing per-day upsert path. */
  onSave: (next: Record<string, number>) => void;
  onClear: () => void;
}) {
  const days = weekDays(weekStart);
  const [draft, setDraft] = useState<Record<string, number>>(hoursByDay);

  // Re-seed the draft whenever the drawer opens on a new cell.
  React.useEffect(() => {
    if (open) setDraft(hoursByDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, weekStart]);

  const total = Object.values(draft).reduce((s, v) => s + (Number(v) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-muted">
          Week of {formatShort(weekStart)} — set uneven daily hours instead of the default even
          Mon–Fri split.
        </div>
        <div className="grid grid-cols-4 gap-3">
          {days.map((d, i) => (
            <div key={d} className="flex flex-col gap-1">
              <Label htmlFor={`day-${d}`}>
                {DAY_LABELS[i]} {formatShort(d)}
              </Label>
              <Input
                id={`day-${d}`}
                type="number"
                min="0"
                step="0.5"
                value={draft[d] ?? 0}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, [d]: parseFloat(e.target.value) || 0 }))
                }
              />
            </div>
          ))}
        </div>
        <div className="text-xs text-muted">Week total: {total.toFixed(1)}h</div>
        <DialogFooter>
          {!readOnly && (
            <Button variant="destructive" onClick={onClear} disabled={saving}>
              Clear week
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button onClick={() => onSave(draft)} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
